<script setup>
import { onBeforeUnmount } from "vue";

const props = defineProps({
  show: Boolean,
  title: { type: String, default: "" },
  message: { type: String, default: "" },
  html: { type: String, default: "" },
  type: { type: String, default: "success" }, // success, error
});

const emit = defineEmits(["close"]);

function close() {
  emit("close");
}

// Auto-close after 3 seconds for success/error
let timeout;
if (props.show) {
  timeout = setTimeout(() => {
    close();
  }, 3000);
}

onBeforeUnmount(() => {
  if (timeout) clearTimeout(timeout);
});
</script>

<template>
  <Transition name="modal">
    <div v-if="show" class="modal-overlay" @click.self="close">
      <div class="modal-container">
        <div class="modal-content">
          <div class="icon-wrapper" :class="type">
            <span class="material-symbols-rounded icon">
              {{ type === 'success' ? 'check_circle' : 'error' }}
            </span>
          </div>
          <h2 class="modal-title" :class="type">{{ title }}</h2>
          <div v-if="html" class="modal-message html-content" v-html="html"></div>
          <p v-else-if="message" class="modal-message">{{ message }}</p>
          <button class="close-btn" :class="type" @click="close">Cerrar</button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* Reuse generic modal styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-container {
  position: relative;
  background: var(--panel-bg, #1e293b);
  border: 1.5px solid var(--panel-border, #334155);
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  width: 100%;
  max-width: 420px;
  overflow: hidden;
  animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  color: var(--text, #f1f5f9);
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(40px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.modal-content {
  padding: 2.5rem 2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
}

/* Icons */
.icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 8px 24px rgba(0,0,0,0.2);
}

.icon-wrapper.success {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  box-shadow: 0 8px 24px rgba(34, 197, 94, 0.3);
}

.icon-wrapper.error {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 8px 24px rgba(239, 68, 68, 0.3);
}

@keyframes scaleIn {
  from { transform: scale(0); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.icon {
  font-size: 3rem;
  color: #fff;
  animation: checkmark 0.6s ease-in-out 0.2s backwards;
}

@keyframes checkmark {
  0% { transform: scale(0) rotate(-45deg); opacity: 0; }
  50% { transform: scale(1.2) rotate(0deg); }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
}

/* Typography */
.modal-title {
  font-size: 1.75rem;
  font-weight: 900;
  margin: 0;
  letter-spacing: -0.02em;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.modal-title.success {
  background-image: linear-gradient(135deg, #22c55e, #16a34a);
}

.modal-title.error {
  background-image: linear-gradient(135deg, #ef4444, #dc2626);
}

.modal-message {
  color: var(--muted, #94a3b8);
  font-size: 1rem;
  margin: 0;
  line-height: 1.6;
  font-weight: 500;
}

.html-content {
  text-align: left;
  width: 100%;
  background: rgba(255,255,255,0.05);
  padding: 1rem;
  border-radius: 12px;
  font-size: 0.9rem;
}

/* Close Button */
.close-btn {
  margin-top: 0.5rem;
  padding: 0.875rem 2rem;
  border: none;
  border-radius: 12px;
  color: #fff;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.close-btn.success {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  box-shadow: 0 4px 16px rgba(34, 197, 94, 0.3);
}

.close-btn.error {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 4px 16px rgba(239, 68, 68, 0.3);
}

.close-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.4);
}
.close-btn:active { transform: translateY(0); }

/* Transitions */
.modal-enter-active, .modal-leave-active { transition: opacity 0.3s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
